import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TablePagination from "@mui/material/TablePagination"
import TableRow from "@mui/material/TableRow"
import { createUseStyles, useTheme } from "react-jss"
import { Box, Typography } from "@mui/material"
import React, { useEffect, useState } from "react"
import { IMaintenanceColumn } from "../../util/interface"
import { RunInfo } from "./RunInfo"
import { ScheduleDefaultRun } from "./ScheduleDefaultRun"
import More from "../../icons/more-btns.svg"
import { useAppDispatch, useAppSelector } from "../../util/hooks"
import {
  changeView,
  maintenanceExecutionHistoryAsync,
  maintenanceListAsync,
  setActiveRunDetails,
} from "./MaintenanceSlice"
import { DefaultConfig } from "./DefaultConfig"
import { Status } from "../../components/Status"
import { SideView } from "./SideView"
import { RunInfoDialogue } from "./RunInfoDialogue"
import {
  colorMapperGantMaintenance,
  maintenanceViews,
  pipeLineStatusIcons,
  textMapperGant,
} from "../../util/StaticData"
import {
  endDateChange,
  marketSinleSelectedValueSelector,
  pipeLineFilterChange,
  pipeLineNamesAndRunIdsAsync,
  runIdChange,
  startDateChange,
  useCaseSelectedValueSelector,
} from "../Monitor/FilterSlice"

const useStyles = createUseStyles({
  wrapper: {
    fontSize: 12,
  },
  root: {
    "& .MuiTableCell-head": {
      backgroundColor: "#fbfbfb",
    },
    height: 85,
  },
  heading: {
    fontSize: 16,
    fontWeight: 600,
  },
  subHeading: {
    color: "#7D7D7D",
    fontSize: 14,
    fontWeight: 600,
  },
  borderRight: {
    borderRight: "1px solid lightgray",
  },
  durationWrapper: {},
  duration: {
    fontSize: 12,
    fontWeight: 500,
    color: "#000000",
    lineHeight: "14px",
  },
  durationPlaceHolder: {
    fontSize: 10,
    color: "#8E8E8E",
  },
  time: {
    color: "#7D7D7D",
    fontSize: 10,
    fontWeight: 500,
  },
  start: {},
  end: {},
  webSiteSubTitle: {
    fontSize: 10,
  },
  webSite: {
    fontSize: 12,
  },
})

const columns: IMaintenanceColumn[] = [
  { id: "usecase", label: "Usecase", minWidth: 170, align: "left" },
  {
    id: "market",
    label: "Market",
    minWidth: 170,
    align: "left",
  },
  {
    id: "pipelineName",
    label: "Pipeline Name",
    minWidth: 170,
    align: "center",
  },
  {
    id: "lastExecuted",
    label: "Last Executed",
    minWidth: 170,
    align: "center",
  },
  {
    id: "duration",
    label: "Duration",
    minWidth: 170,
    align: "center",
  },
  {
    id: "status",
    label: "Status",
    minWidth: 170,
    align: "left",
  },
]

export const Maintenance = () => {
  const [page, setPage] = React.useState(0) // State for tracking the current page in pagination
  const [rowsPerPage, setRowsPerPage] = React.useState(10) // State for the number of rows displayed per page
  const theme: any = useTheme() // Hook to access theme variables
  const classes = useStyles({ theme }) // Custom styles using JSS

  // Select maintenance list data from the Redux store
  const maintenanceListData = useAppSelector(
    (store) => store.maintenance.maintenanceListData,
  )
  const [tableData, setTableData] = useState(maintenanceListData) // State for holding filtered table data

  // Handle page change in pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage) // Update the current page state
  }

  // Handle change in the number of rows per page
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value) // Update rows per page
    setPage(0) // Reset to the first page
  }

  const dispatch = useAppDispatch() // Hook to get the dispatch function

  // Function to open the dialog for a specific run
  const openDialog = (runDetails: any) => {
    dispatch(setActiveRunDetails(runDetails)) // Set the active run details
    dispatch(maintenanceExecutionHistoryAsync()) // Fetch execution history
    dispatch(changeView(true)) // Change the view to show details
  }

  // Select various state values from the Redux store
  const activeRunId = useAppSelector(
    (store) => store.maintenance.activeRunDetails?.pipelineId,
  )
  const sideView = useAppSelector((store) => store.maintenance.sideView)
  const useCaseSelectedValue = useAppSelector(useCaseSelectedValueSelector)
  const marketSingleSelectedValue = useAppSelector(
    marketSinleSelectedValueSelector,
  )
  const startDate = useAppSelector((state) => state.filters.startDate)
  const endDate = useAppSelector((state) => state.filters.endDate)

  // Effect to fetch maintenance list data based on selected filters
  useEffect(() => {
    if (
      (useCaseSelectedValue && marketSingleSelectedValue) ||
      (startDate && endDate)
    ) {
      dispatch(maintenanceListAsync()) // Fetch maintenance list
      if (activeRunId) {
        dispatch(maintenanceExecutionHistoryAsync()) // Fetch execution history if active run ID exists
      }
    }
  }, [
    useCaseSelectedValue,
    marketSingleSelectedValue,
    dispatch,
    startDate,
    endDate,
    activeRunId,
  ])

  // Effect to fetch pipeline names and run IDs when use case and market are selected
  useEffect(() => {
    if (useCaseSelectedValue && marketSingleSelectedValue) {
      dispatch(pipeLineNamesAndRunIdsAsync())
    }
  }, [useCaseSelectedValue, marketSingleSelectedValue, dispatch])

  // Effect to reset filters on component mount
  useEffect(() => {
    dispatch(startDateChange("")) // Reset start date
    dispatch(endDateChange("")) // Reset end date
    dispatch(pipeLineFilterChange("")) // Reset pipeline filter
    dispatch(runIdChange("")) // Reset run ID filter
  }, [])

  // Select the currently selected pipeline value
  const pipeLineSelectedValue = useAppSelector(
    (store) => store.filters.pipeLineSelectedValue,
  )

  // Effect to filter table data based on the selected pipeline
  useEffect(() => {
    if (pipeLineSelectedValue) {
      const filteredTableData = maintenanceListData.filter(
        (obj: any) => obj.pipelineName === pipeLineSelectedValue,
      )
      setTableData(filteredTableData) // Update table data with filtered results
    } else {
      setTableData(maintenanceListData) // Reset to the full list if no pipeline is selected
    }
  }, [pipeLineSelectedValue, maintenanceListData])

  // Render the component
  return (
    <Box sx={{ paddingTop: 2 }}>
      <ScheduleDefaultRun />
      <DefaultConfig />
      <RunInfoDialogue>
        <RunInfo currentView={maintenanceViews.dialogView} />
      </RunInfoDialogue>
      <Box display={"flex"} gap={sideView ? 2 : 0}>
        <Paper sx={{ width: sideView ? "60%" : "100%" }}>
          <TableContainer sx={{ maxHeight: 515, borderRadius: 10 }}>
            <Table
              stickyHeader
              aria-label="sticky table"
              sx={{ position: "relative", zIndex: 0 }}
            >
              <TableHead className={classes.root}>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      className={classes.wrapper}
                    >
                      <Typography variant="tableHeaderMaintenance">
                        {column.label}
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell
                    key={"Maintenance"}
                    align={"center"}
                    className={classes.wrapper}
                  >
                    <Typography variant="tableHeaderMaintenance">
                      Maintenance
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                      {columns.map((column) => {
                        const value = row[column.id] // Get the value for the current column

                        if (column.id === "duration") {
                          return (
                            <TableCell
                              className={classes.wrapper}
                              key={column.id}
                              align={column.align}
                            >
                              <div className={classes.durationWrapper}>
                                <div className={classes.duration}>{value}</div>
                                <div className={classes.durationPlaceHolder} />
                              </div>
                            </TableCell>
                          )
                        }

                        if (column.id === "status") {
                          return (
                            <TableCell
                              className={classes.wrapper}
                              key={column.id}
                              align={column.align}
                            >
                              <Status
                                backgroundColor={
                                  colorMapperGantMaintenance[value]
                                }
                                text={textMapperGant[value]}
                                icon={pipeLineStatusIcons[value]}
                                align={column.align}
                              />
                            </TableCell>
                          )
                        }

                        return (
                          <TableCell
                            className={classes.wrapper}
                            key={column.id}
                            align={column.align}
                          >
                            <Box sx={{ fontSize: 12 }}>{value}</Box>
                          </TableCell>
                        )
                      })}
                      <TableCell
                        className={classes.wrapper}
                        key={"maintenance"}
                        align={"center"}
                        sx={{ cursor: "pointer" }}
                        onClick={() => openDialog(row)} // Open dialog for the clicked row
                      >
                        <Box display="flex" justifyContent={"center"}>
                          <img src={More} alt="" />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20]} // Options for rows per page
            component="div"
            count={maintenanceListData.length} // Total count of items for pagination
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage} // Handler for page change
            onRowsPerPageChange={handleChangeRowsPerPage} // Handler for rows per page change
          />
        </Paper>
        {sideView ? (
          <Paper sx={{ width: "40%" }}>
            <SideView>
              <RunInfo currentView={maintenanceViews.sideview} />
            </SideView>
          </Paper>
        ) : (
          ""
        )}
      </Box>
    </Box>
  )
}
